import { useCallback, useMemo, useState } from 'react';

import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Slide,
  Typography,
} from '@material-ui/core';
import { alpha, makeStyles } from '@material-ui/core/styles';
import FolderIcon from '@material-ui/icons/Folder';
import FolderOpenIcon from '@material-ui/icons/FolderOpen';
import clsx from 'clsx';
import { useDebouncedCallback } from 'use-debounce';

import DocumentPdfImage from 'assets/images/document-pdf.svg';
import DialogHeader from 'components/DialogHeader';
import EmptyState from 'components/EmptyState';
import InfiniteScroll from 'components/InfiniteScroll';
import SearchBar from 'components/SearchBar';
import { useAppDispatch, useAppSelector } from 'store';
import {
  fetchArquivosByGaleria,
  fetchGalerias,
  galeriasSelector,
  resetArquivosGaleria,
  resetGalerias,
} from 'store/reducers/galerias';

type ModalGaleriaProps = {
  open: boolean;
  isInstagramOrMessengerChannel?: boolean;
  onClose: () => void;
  onSendFiles?: (storageIds: string[]) => void;
};

const QTD_MAX_ARQUIVOS_POR_VEZ = 10; // Quantidade maxima de arquivos a serem enviados de cada vez
const LIMIT_PAGINACAO_ARQUIVOS = 50;
const ARQUIVOS_SCROLL_ID = 'modal-galeria-lista-arquivos';

const useStyles = makeStyles(({ palette, shape, spacing }) => ({
  dialogPaper: {
    height: '80%',
    overflow: 'hidden',
    position: 'relative',
  },
  dialogContent: {
    overflow: 'hidden',
    position: 'relative',
    padding: spacing(1, 3, 0),
  },
  dialogActions: {
    padding: 0,
    marginTop: 0,
    color: palette.common.white,
    backgroundColor: palette.primary.main,
    '& > *': {
      padding: spacing(2, 0),
    },
  },
  list: {
    height: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingRight: spacing(1),
  },
  filesList: {
    maxWidth: '100%',
    overflowX: 'hidden',
    '& > div': {
      maxWidth: '100%',
      overflowX: 'hidden',
    },
  },
  listItem: {
    borderRadius: shape.borderRadius,
    border: `1px solid ${palette.divider}`,
    marginBottom: spacing(1),
  },
  cardContentSelected: {
    color: palette.primary.main,
    backgroundColor: alpha(palette.primary.main, 0.1),
  },
  cardSelected: {
    border: `2px solid ${palette.primary.main}`,
  },
  cardMedia: {
    height: 0,
    paddingTop: '56.25%', // 16:9
    overflow: 'hidden',
    position: 'relative',
  },
  sendButton: {
    color: palette.getContrastText(palette.primary.main),
  },
}));

export default function ModalGaleria(props: ModalGaleriaProps) {
  const {
    open,
    isInstagramOrMessengerChannel = false,
    onClose,
    onSendFiles,
  } = props;
  const classes = useStyles();
  const [galeriaSelectedId, setGaleriaSelectedId] = useState('');
  const [searchArquivos, setSearchArquivos] = useState('');
  const [arquivosSelectedIds, setArquivosSelectedIds] = useState(new Set());
  const dispatch = useAppDispatch();
  const {
    galerias,
    arquivosGaleria,
    arquivosGaleriaPagination,
    loadingGalerias,
    loadingArquivosGaleria,
  } = useAppSelector(galeriasSelector);

  const filteredArquivosGaleria = useMemo(
    () =>
      isInstagramOrMessengerChannel
        ? arquivosGaleria.filter(arquivo =>
            arquivo.storage.mimeType.startsWith('image/'),
          )
        : arquivosGaleria,
    [arquivosGaleria, isInstagramOrMessengerChannel],
  );

  const hasMoreArquivos =
    arquivosGaleria.length < arquivosGaleriaPagination.total;

  const fetchNextArquivos = useCallback(() => {
    if (loadingArquivosGaleria || !hasMoreArquivos || !galeriaSelectedId)
      return;

    dispatch(
      fetchArquivosByGaleria({
        galeriaId: galeriaSelectedId,
        search: searchArquivos || undefined,
        page: arquivosGaleriaPagination.page + 1,
        limit: LIMIT_PAGINACAO_ARQUIVOS,
      }),
    );
  }, [
    arquivosGaleriaPagination.page,
    dispatch,
    galeriaSelectedId,
    hasMoreArquivos,
    loadingArquivosGaleria,
    searchArquivos,
  ]);

  const handleSelectGaleria = (galeriaId: string) => {
    const arquivoIds = new Set(arquivosSelectedIds);

    if (arquivoIds.size > 0) arquivoIds.clear();

    setArquivosSelectedIds(arquivoIds);
    setGaleriaSelectedId(galeriaId);
    setSearchArquivos('');

    dispatch(resetArquivosGaleria());
    dispatch(
      fetchArquivosByGaleria({
        galeriaId,
        page: 1,
        limit: LIMIT_PAGINACAO_ARQUIVOS,
      }),
    );
  };

  const handleSelectArquivo = (arquivoId: string) => {
    const arquivoIds = new Set(arquivosSelectedIds);

    // verifica se o arquivo ja foi selecionado
    if (arquivoIds.has(arquivoId)) {
      arquivoIds.delete(arquivoId);
      // verifica se a quantidade máxima de arquivos selecionados por vez ja foi atingida
    } else if (arquivoIds.size < QTD_MAX_ARQUIVOS_POR_VEZ) {
      arquivoIds.add(arquivoId);
    }

    setArquivosSelectedIds(arquivoIds);
  };

  const handleSearch = (value: string) => {
    if (!galeriaSelectedId) return;

    setSearchArquivos(value);
    dispatch(resetArquivosGaleria());
    dispatch(
      fetchArquivosByGaleria({
        galeriaId: galeriaSelectedId,
        search: value,
        page: 1,
        limit: LIMIT_PAGINACAO_ARQUIVOS,
      }),
    );
  };

  const handleCancelSearch = () => {
    if (!galeriaSelectedId) return;

    setSearchArquivos('');
    dispatch(resetArquivosGaleria());
    dispatch(
      fetchArquivosByGaleria({
        galeriaId: galeriaSelectedId,
        page: 1,
        limit: LIMIT_PAGINACAO_ARQUIVOS,
      }),
    );
  };

  const handleSubmitFilesDebounced = useDebouncedCallback(
    (values: string[]) => {
      if (onSendFiles) onSendFiles(values);
    },
    300,
  );

  const handleClickButton = () => {
    if (arquivosSelectedIds.size <= 0) return;

    if (onSendFiles) {
      const values = Array.from(arquivosSelectedIds) as string[];
      handleSubmitFilesDebounced(values);
    }

    onClose();
  };

  const renderGalerias = galerias.map(galeria => {
    const key = galeria.id;
    const selected = galeriaSelectedId === galeria.id;

    return (
      <ListItem
        key={key}
        className={classes.listItem}
        onClick={selected ? undefined : () => handleSelectGaleria(galeria.id)}
        selected={selected}
        button
      >
        <ListItemIcon>
          {selected ? <FolderOpenIcon /> : <FolderIcon />}
        </ListItemIcon>

        <ListItemText
          primary={galeria.name}
          primaryTypographyProps={{ noWrap: true, title: galeria.name }}
        />
      </ListItem>
    );
  });

  const renderArquivos = filteredArquivosGaleria.map(arquivo => {
    const selected = arquivosSelectedIds.has(arquivo.storage.id);
    const isImage = arquivo.storage.mimeType.startsWith('image/');

    return (
      <Grid key={arquivo.storage.id} item xs={3}>
        <Card
          className={clsx({ [classes.cardSelected]: selected })}
          title={arquivo.storage.fileName}
          variant={selected ? 'elevation' : 'outlined'}
        >
          <CardActionArea
            onClick={() => handleSelectArquivo(arquivo.storage.id)}
          >
            {arquivo.storage.internalUrl && isImage && (
              <CardMedia
                image={arquivo.storage.internalUrl}
                className={classes.cardMedia}
              />
            )}

            {!isImage && (
              <CardMedia
                image={DocumentPdfImage}
                className={classes.cardMedia}
              />
            )}

            <CardContent
              className={clsx({ [classes.cardContentSelected]: selected })}
            >
              <Typography variant="caption" component="p" noWrap>
                {arquivo.storage.fileName}
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>
      </Grid>
    );
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
      TransitionProps={{
        onEnter: () => {
          dispatch(resetGalerias());
          dispatch(fetchGalerias());
        },
        onExited: () => {
          setGaleriaSelectedId('');
          setSearchArquivos('');
          setArquivosSelectedIds(new Set());
          dispatch(resetArquivosGaleria());
          dispatch(resetGalerias());
        },
      }}
    >
      <DialogHeader
        title="Galeria"
        description={`Selecione e envie até ${QTD_MAX_ARQUIVOS_POR_VEZ} arquivos por vez da sua galeria`}
        onClose={onClose}
      />

      <DialogContent className={classes.dialogContent}>
        {loadingGalerias && galerias.length <= 0 && (
          <EmptyState icon={<CircularProgress color="inherit" size={32} />} />
        )}

        {!loadingGalerias && galerias.length <= 0 && (
          <EmptyState
            title="A sua galeria está vazia"
            description='"Não encontramos nenhuma pasta de arquivos na sua galeria"'
          />
        )}

        {galerias.length > 0 && (
          <Grid container spacing={1} component={Box} height="100%">
            <Grid item xs={4} component={Box} height="100%" overflow="hidden">
              <List className={classes.list} disablePadding dense>
                {renderGalerias}
              </List>
            </Grid>

            <Grid item xs component={Box} height="100%" position="relative">
              {!galeriaSelectedId && (
                <EmptyState
                  title="Selecione uma galeria ao lado"
                  description='"Todos os arquivos da galeria aparecerão aqui"'
                />
              )}

              {galeriaSelectedId && (
                <List
                  id={ARQUIVOS_SCROLL_ID}
                  className={clsx(classes.list, classes.filesList)}
                  disablePadding
                  dense
                >
                  <InfiniteScroll
                    data={arquivosGaleria}
                    next={fetchNextArquivos}
                    hasMore={hasMoreArquivos}
                    loading={loadingArquivosGaleria}
                    scrollableTarget={ARQUIVOS_SCROLL_ID}
                  >
                    <Box mb={2}>
                      <SearchBar
                        key={galeriaSelectedId}
                        value={searchArquivos}
                        onChange={handleSearch}
                        onCancelSearch={handleCancelSearch}
                        placeholder="Procurar arquivo por nome"
                      />
                    </Box>

                    <Grid container spacing={2}>
                      {renderArquivos}

                      {!loadingArquivosGaleria &&
                        filteredArquivosGaleria.length <= 0 && (
                          <Grid item>
                            <Typography variant="body2" color="textSecondary">
                              Não encontramos nenhum arquivo nesta galeria
                            </Typography>
                          </Grid>
                        )}
                    </Grid>
                  </InfiniteScroll>
                </List>
              )}
            </Grid>
          </Grid>
        )}
      </DialogContent>

      {arquivosSelectedIds.size > 0 && (
        <Slide direction="up" in>
          <DialogActions className={classes.dialogActions} disableSpacing>
            <Button
              fullWidth
              className={classes.sendButton}
              onClick={handleClickButton}
            >
              {`ENVIAR ${arquivosSelectedIds.size} de ${QTD_MAX_ARQUIVOS_POR_VEZ}`}
            </Button>
          </DialogActions>
        </Slide>
      )}
    </Dialog>
  );
}
